/**
 * Analytics — PostHog Integration
 *
 * Centralised wrapper around PostHog for product analytics.
 * Provides typed event tracking and user identification so callers
 * never import posthog-js directly.
 *
 * Initialisation:
 *   PostHog is initialised via <PostHogProvider> in main.tsx.
 *   The provider reads VITE_POSTHOG_KEY and VITE_POSTHOG_HOST automatically.
 *   If VITE_POSTHOG_KEY is not set, <App /> renders without the provider
 *   and all tracking calls below become safe no-ops.
 *
 * Environment variables (add to .env and Cloudflare Pages):
 *   VITE_POSTHOG_KEY  — PostHog project API key (starts with phc_)
 *   VITE_POSTHOG_HOST — PostHog ingest URL (https://us.i.posthog.com)
 *
 * PostHog setup:
 *   1. Create a free account at https://posthog.com
 *   2. Create a project → copy the API key
 *   3. Add VITE_POSTHOG_KEY=phc_xxx and VITE_POSTHOG_HOST=https://us.i.posthog.com to .env
 *
 * Privacy notes:
 *   - IP anonymisation: enabled (ip: false in PostHogProvider options)
 *   - Session recording: disabled (disable_session_recording: true)
 *   - No PII is sent — only role, org_id, and event metadata
 *   - Analytics are disabled when VITE_POSTHOG_KEY is not set
 *
 * Development:
 *   In dev mode, PostHog is loaded but capturing is opt_out'd by default
 *   to avoid polluting production data. Override by setting:
 *     VITE_POSTHOG_FORCE_ENABLE=true
 *   in your .env file.
 */

import posthog from "posthog-js";

// ─── Typed Event Catalogue ────────────────────────────────────────────────────

export type AnalyticsEvent =
  // Auth
  | "user_signed_up"
  | "user_signed_in"
  | "user_signed_out"
  // Onboarding
  | "onboarding_completed"
  // Hosts
  | "host_registered"
  | "host_approved"
  | "host_rejected"
  | "owner_verified"          // BC Registry permit confirmed valid
  // Bookings
  | "booking_created"
  | "booking_approved"
  | "booking_blocked"
  | "booking_cancelled"
  // Guest portal
  | "guest_checkin_started"
  | "guest_checkin_completed"
  // Billing
  | "subscription_started"
  | "subscription_cancelled"
  | "payment_succeeded";      // successful Stripe payment (checkout or renewal)

export type AnalyticsProperties = Record<string, string | number | boolean | null | undefined>;

// ─── Init (no-op — PostHog is initialised via <PostHogProvider> in main.tsx) ──

/**
 * @deprecated PostHog is now initialised via <PostHogProvider> in main.tsx.
 * This function is kept as a no-op for backward compatibility.
 * It is safe to call and safe to remove callers of.
 */
export function initAnalytics(): void {
  // No-op. PostHog initialisation is handled by <PostHogProvider> in main.tsx.
}

// ─── Identify User ────────────────────────────────────────────────────────────

/**
 * Associate subsequent events with a user identity.
 * Only sends role and org — never email, name, or other PII.
 * Safe to call before PostHog is loaded (fails silently).
 */
export function identifyUser(params: {
  userId: string;
  role: string;
  primaryOrgId?: string;
}): void {
  try {
    posthog.identify(params.userId, {
      role:           params.role,
      primary_org_id: params.primaryOrgId ?? null,
    });
  } catch {
    // PostHog not yet initialised or VITE_POSTHOG_KEY not set — safe to ignore
  }
}

/**
 * Clear the PostHog identity on sign-out.
 * Safe to call before PostHog is loaded (fails silently).
 */
export function resetAnalyticsUser(): void {
  try {
    posthog.reset();
  } catch {
    // PostHog not yet initialised or VITE_POSTHOG_KEY not set — safe to ignore
  }
}

// ─── Track Event ──────────────────────────────────────────────────────────────

/**
 * Track a product analytics event.
 * Fire-and-forget — never throws, never blocks the caller.
 * Safe to call before PostHog is loaded (fails silently).
 *
 * @param event       One of the typed AnalyticsEvent strings
 * @param properties  Optional additional properties (no PII)
 */
export function trackEvent(
  event: AnalyticsEvent,
  properties?: AnalyticsProperties
): void {
  try {
    posthog.capture(event, properties);
  } catch (err) {
    // Analytics must never break the application
    console.warn("[Analytics] trackEvent failed:", err);
  }
}
